import React, { useState, useEffect } from 'react';
import api from '../api';
import { PaperPlaneTilt, CheckCircle, ArrowClockwise, Clock, ChatText } from '@phosphor-icons/react';

const ACTION_STYLES = {
  submit: { icon: PaperPlaneTilt, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Submitted' },
  approve: { icon: CheckCircle, color: 'text-status-on-track', bg: 'bg-status-on-track-bg', label: 'Approved' },
  rework: { icon: ArrowClockwise, color: 'text-status-alert', bg: 'bg-status-alert-bg', label: 'Sent for Rework' },
};

export default function WorkflowPanel({ recordId, recordType, currentStatus, onUpdate }) {
  const [logs, setLogs] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    if (!recordId) return;
    try {
      const { data } = await api.get(`/api/workflow/log/${recordId}`);
      setLogs(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchLogs(); }, [recordId]);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const endpoint = action === 'submit' ? '/api/workflow/submit' : '/api/workflow/review';
      const payload = { record_id: recordId, record_type: recordType, comment };
      if (action !== 'submit') payload.action = action;
      const { data } = await api.post(endpoint, payload);
      setComment('');
      fetchLogs();
      if (onUpdate) onUpdate(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !currentStatus || currentStatus === 'Draft' || currentStatus === 'Rework';
  const canReview = currentStatus === 'Submitted';

  return (
    <div data-testid="workflow-panel" className="bg-white border border-border-default rounded-lg">
      <div className="px-5 py-3 border-b border-border-default">
        <h4 className="text-sm font-semibold text-content-primary font-heading">Workflow</h4>
      </div>

      {/* Actions */}
      <div className="px-5 py-3 space-y-3">
        <div>
          <label className="block text-[10px] font-semibold text-content-secondary uppercase tracking-wider mb-1">Comment</label>
          <textarea
            data-testid="workflow-comment"
            value={comment}
            onChange={e => setComment(e.target.value)}
            className="w-full px-3 py-2 border border-border-default rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-secondary/50 resize-none"
            rows={2}
            placeholder="Add a comment (optional)..."
          />
        </div>
        <div className="flex gap-2">
          {canSubmit && (
            <button
              data-testid="workflow-submit-btn"
              onClick={() => handleAction('submit')}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              <PaperPlaneTilt size={14} /> Submit
            </button>
          )}
          {canReview && (
            <>
              <button
                data-testid="workflow-approve-btn"
                onClick={() => handleAction('approve')}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 bg-status-on-track text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-all disabled:opacity-50"
              >
                <CheckCircle size={14} /> Approve
              </button>
              <button
                data-testid="workflow-rework-btn"
                onClick={() => handleAction('rework')}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 bg-status-alert text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-all disabled:opacity-50"
              >
                <ArrowClockwise size={14} /> Rework
              </button>
            </>
          )}
          {currentStatus === 'Approved' && (
            <span className="flex items-center gap-1.5 px-3 py-2 bg-status-on-track-bg text-status-on-track rounded-lg text-xs font-semibold">
              <CheckCircle size={14} weight="fill" /> Approved
            </span>
          )}
        </div>
      </div>

      {/* Log */}
      {logs.length > 0 && (
        <div className="px-5 pb-3">
          <p className="text-[10px] font-semibold text-content-disabled uppercase tracking-wider mb-2">History</p>
          <div className="space-y-2 max-h-[150px] overflow-y-auto">
            {logs.map((log, i) => {
              const style = ACTION_STYLES[log.action] || ACTION_STYLES.submit;
              const Icon = style.icon;
              return (
                <div key={i} className="flex items-start gap-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${style.bg}`}>
                    <Icon size={10} className={style.color} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-content-primary">
                      <span className="font-medium">{log.performed_by_name || 'User'}</span>
                      <span className="text-content-disabled"> {style.label}</span>
                    </p>
                    {log.comment && <p className="text-[10px] text-content-secondary mt-0.5">{log.comment}</p>}
                    <p className="text-[9px] text-content-disabled">{new Date(log.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
